import type { ReactNode } from "react";
import type { ImportResult } from "../types/transaction";

interface ImportResultPanelProps {
  result: ImportResult;
  /** The file's name, shown first when more than one file was sent this session. */
  fileName?: string | null;
}

/**
 * The upload's answer in the server's own words (LF-142).
 *
 * Renders, in order: the server's `summary`, each of its `notes`, the
 * unnamed-accounts `notice`, and the rows it could not read. Every sentence is
 * the server's, verbatim; the one word this component composes is the row
 * label "Row N:", counted from 1 as the owner counts. Each list defaults to
 * empty, so an older response without `notes`, `errors` or `unnamed_accounts`
 * renders the summary alone. An empty summary renders nothing in its place:
 * no fallback sentence is invented. When `fileName` is given (the second
 * file of a session onward) it comes before every sentence.
 */
export function ImportResultPanel({ result, fileName = null }: ImportResultPanelProps): ReactNode {
  const notes = result.notes ?? [];
  const errors = result.errors ?? [];
  const notice = result.unnamed_accounts?.notice ?? null;

  return (
    <>
      {fileName && <p className="upload-file-name">{fileName}</p>}
      {result.summary && <p className="upload-done-summary">{result.summary}</p>}
      {notes.map((note, position) => (
        <p className="upload-done-note" key={`note-${position}`}>
          {note}
        </p>
      ))}
      {notice && <p className="upload-done-note">{notice}</p>}
      {errors.length > 0 && (
        <ul className="upload-done-errors">
          {/* The index restarts in each statement of a file, so the list
              position is the only key known to be unique. */}
          {errors.map((rowError, position) => (
            <li key={`error-${position}`}>{`Row ${rowError.index + 1}: ${rowError.error}`}</li>
          ))}
        </ul>
      )}
    </>
  );
}
